import React, { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import type { HotkeyRegistryEntry } from "capitalrx-hotkeys";

import { Modal } from "../../Modal";
import { COMPONENT_CLASSES, DEFAULT_COMPONENT_CONFIG, DISPLAY_NAMES, NAVIGATION_KEYS } from "../constants";
import type { CommandPaletteProps } from "../types";
import { classNames, debounce, fuzzyMatch, groupBy } from "../utils";
import { HotkeyBadge } from "./HotkeyBadge";
import { HotkeyErrorBoundary } from "./HotkeyErrorBoundary";
import { useHotkeyContext } from "./HotkeyProvider";

interface Command {
  id: string;
  title: string;
  description: string;
  category: string;
  icon?: ReactNode;
  shortcut: string;
  disabled: boolean;
  handler: () => void;
}

interface CommandWithScore {
  readonly command: Command;
  readonly score: number;
}

export function CommandPalette({
  isOpen = false,
  onOpenChange,
  placeholder = "Type a command or search...",
  showIcons = true,
  showShortcuts = true,
  maxResults = DEFAULT_COMPONENT_CONFIG.commandPalette.maxResults,
  filterFn,
  sortFn,
  groupByCategory = true,
  showRecent = true,
  onExecute,
  renderCommand,
  zIndex,
}: CommandPaletteProps): ReactNode {
  const { manager } = useHotkeyContext();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentCommands, setRecentCommands] = useState<Command[]>([]);
  const [registryVersion, setRegistryVersion] = React.useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track registry changes to invalidate memoized commands
  useEffect(() => {
    const handleRegistryChange = () => {
      setRegistryVersion((v) => v + 1);
    };

    const unsubscribeRegister = manager.on?.("hotkey:registered", handleRegistryChange);
    const unsubscribeUnregister = manager.on?.("hotkey:unregistered", handleRegistryChange);

    return () => {
      unsubscribeRegister?.();
      unsubscribeUnregister?.();
    };
  }, [manager]);

  // Convert hotkeys to commands for display in palette
  const commands = React.useMemo(() => {
    const hotkeys = manager.getHotkeys?.() || [];
    return hotkeys
      .filter((entry: HotkeyRegistryEntry) => {
        // Check if there's a description (which may contain the title)
        return entry.config.description;
      })
      .map((entry: HotkeyRegistryEntry) => {
        const config = entry.config as HotkeyRegistryEntry["config"] & {
          title?: string;
          category?: string;
          handler?: (event: unknown) => void;
        };
        const modifiers = config.modifiers || [];
        const shortcut =
          modifiers.length > 0
            ? `${modifiers.map((m: string) => m.toUpperCase()).join("+")}+${config.key.toUpperCase()}`
            : config.key.toUpperCase();

        // Use title if available (from React wrapper), otherwise fall back to description
        const title = config.title || config.description || "Unnamed";
        const description = config.description || title;
        const category = config.category || "Other";

        return {
          id: entry.id,
          title,
          description,
          category,
          shortcut,
          disabled: !entry.enabled,
          handler: () => {
            // Only trigger if enabled
            if (entry.enabled && config.handler) {
              /**
               * Create a synthetic KeyboardEvent for command palette triggers.
               * This is needed because handlers expect originalEvent to be a KeyboardEvent.
               */
              const syntheticEvent = new KeyboardEvent("keydown", {
                key: config.key,
                code: `Key${config.key.toUpperCase()}`,
                ctrlKey: config.modifiers?.includes("ctrl") ?? false,
                shiftKey: config.modifiers?.includes("shift") ?? false,
                altKey: config.modifiers?.includes("alt") ?? false,
                metaKey: config.modifiers?.includes("meta") ?? false,
                bubbles: true,
                cancelable: true,
              });

              // Create proper normalized event structure expected by handler
              const normalizedEvent = {
                key: config.key,
                code: config.key,
                modifiers: new Set(config.modifiers || []),
                originalEvent: syntheticEvent,
                timestamp: Date.now(),
                preventDefault: () => syntheticEvent.preventDefault(),
                stopPropagation: () => syntheticEvent.stopPropagation(),
              };
              config.handler(normalizedEvent);
            }
          },
        };
      });
  }, [manager, registryVersion]);

  const filteredCommands = React.useMemo(() => {
    if (!query) return commands.slice(0, maxResults);

    const results = commands
      .map((cmd: Command) => {
        if (filterFn) {
          return filterFn(cmd, query) ? { command: cmd, score: 1 } : null;
        }

        const titleMatch = fuzzyMatch(query, cmd.title || "");
        const descMatch = fuzzyMatch(query, cmd.description || "");

        if (!titleMatch.matches && !descMatch.matches) return null;

        return {
          command: cmd,
          score: Math.max(titleMatch.score, descMatch.score),
        };
      })
      .filter((item: CommandWithScore | null): item is CommandWithScore => item !== null)
      .sort((a: CommandWithScore, b: CommandWithScore) => {
        if (sortFn) return sortFn(a.command, b.command, query);
        return b.score - a.score;
      })
      .slice(0, maxResults)
      .map((r: CommandWithScore) => r.command);

    return results;
  }, [commands, query, maxResults, filterFn, sortFn]);

  const groupedCommands = React.useMemo(() => {
    if (!groupByCategory) return { All: filteredCommands };
    return groupBy(filteredCommands, (cmd: Command) => cmd.category || "Other");
  }, [filteredCommands, groupByCategory]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset selection when query or filtered commands change
  const prevQueryRef = useRef(query);
  const prevFilteredCommandsLengthRef = useRef(filteredCommands.length);
  useEffect(() => {
    if (prevQueryRef.current !== query || prevFilteredCommandsLengthRef.current !== filteredCommands.length) {
      setSelectedIndex(0);
      prevQueryRef.current = query;
      prevFilteredCommandsLengthRef.current = filteredCommands.length;
    }
  }, [query, filteredCommands.length]);

  const executeCommand = useCallback(
    (command: Command) => {
      command.handler?.();
      onExecute?.(command);

      setRecentCommands((prev) => {
        const filtered = prev.filter((c) => c.id !== command.id);
        return [command, ...filtered].slice(0, DEFAULT_COMPONENT_CONFIG.commandPalette.recentCommandsLimit);
      });

      onOpenChange?.(false);
      setQuery("");
    },
    [onExecute, onOpenChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case NAVIGATION_KEYS.DOWN:
          event.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, filteredCommands.length - 1));
          break;
        case NAVIGATION_KEYS.UP:
          event.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case NAVIGATION_KEYS.ENTER:
          event.preventDefault();
          if (filteredCommands[selectedIndex]) {
            executeCommand(filteredCommands[selectedIndex]);
          }
          break;
        case NAVIGATION_KEYS.ESCAPE:
          event.preventDefault();
          onOpenChange?.(false);
          break;
        default:
          break;
      }
    },
    [filteredCommands, selectedIndex, onOpenChange, executeCommand],
  );

  const handleInputChange = debounce((value: string) => setQuery(value), DEFAULT_COMPONENT_CONFIG.commandPalette.debounceDelay);

  return (
    <HotkeyErrorBoundary>
      <Modal visible={isOpen} size="wide" title="Command Palette" onClose={() => onOpenChange?.(false)}>
        <div className={COMPONENT_CLASSES.palette.container} style={{ zIndex }}>
          <input
            ref={inputRef}
            type="text"
            className={COMPONENT_CLASSES.palette.input}
            placeholder={placeholder}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />

          <div className={COMPONENT_CLASSES.palette.results}>
            {showRecent && !query && recentCommands.length > 0 && (
              <div className={COMPONENT_CLASSES.palette.category}>
                <h3>Recent</h3>
                {recentCommands.map((cmd, index) => (
                  <CommandItem
                    key={cmd.id || index}
                    command={cmd}
                    isSelected={false}
                    showIcon={showIcons}
                    showShortcut={showShortcuts}
                    onClick={() => executeCommand(cmd)}
                    renderCommand={renderCommand}
                  />
                ))}
              </div>
            )}

            {Object.keys(groupedCommands).length === 0 ? (
              <div className={COMPONENT_CLASSES.palette.empty}>No commands found matching "{query}"</div>
            ) : (
              Object.entries(groupedCommands).map(([category, cmds]) => (
                <div key={category} className={COMPONENT_CLASSES.palette.category}>
                  {groupByCategory && <h3>{category}</h3>}
                  {(cmds as Command[]).map((cmd, index) => {
                    const globalIndex = filteredCommands.indexOf(cmd);
                    return (
                      <CommandItem
                        key={cmd.id || index}
                        command={cmd}
                        isSelected={globalIndex === selectedIndex}
                        showIcon={showIcons}
                        showShortcut={showShortcuts}
                        onClick={() => executeCommand(cmd)}
                        renderCommand={renderCommand}
                      />
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>
    </HotkeyErrorBoundary>
  );
}

CommandPalette.displayName = DISPLAY_NAMES.CommandPalette;

interface CommandItemProps {
  command: Command;
  isSelected: boolean;
  showIcon: boolean;
  showShortcut: boolean;
  onClick: () => void;
  renderCommand?: (command: Command) => ReactNode;
}

function CommandItem({ command, isSelected, showIcon, showShortcut, onClick, renderCommand }: CommandItemProps) {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!command.disabled) {
        onClick();
      }
    }
  };

  if (renderCommand) {
    return (
      <button type="button" onClick={onClick} onKeyDown={handleKeyDown} className={COMPONENT_CLASSES.palette.command}>
        {renderCommand(command)}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={classNames(
        COMPONENT_CLASSES.palette.command,
        isSelected && COMPONENT_CLASSES.palette.commandSelected,
        command.disabled && "command-palette__command--disabled",
      )}
      onClick={command.disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      disabled={command.disabled}
      aria-disabled={command.disabled}
      style={command.disabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
    >
      {showIcon && command.icon && <span className={COMPONENT_CLASSES.palette.icon}>{command.icon}</span>}
      <div>
        <div>{command.title}</div>
        {command.description && <div style={{ fontSize: "0.875em", opacity: 0.7 }}>{command.description}</div>}
      </div>
      {showShortcut && command.shortcut && (
        <div className={COMPONENT_CLASSES.palette.shortcut}>
          <HotkeyBadge combo={command.shortcut} theme="minimal" size="xs" disabled={command.disabled} />
        </div>
      )}
    </button>
  );
}
